import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import {
  CONSOLE_INTL_ONBOARDING_DOC_URL,
  CONSOLE_RANGE_OPTIONS,
  CONSOLE_DEFAULT_RANGE,
  getConsoleCityUrl,
} from "../../utils/consoleUtils";
import "../../styles/console.css";

const CITY_STAGES = ["planned", "seeding", "runnable", "stable"];
const CITY_STATUS_LABELS = {
  stable: "Stable",
  runnable: "Runnable",
  seeding: "Seeding",
  planned: "Planned",
};

/** Derive display status from coverage (cities are from clubs; we don't persist status). Returns null when score/runs are missing. */
function deriveStatus(city) {
  const score = city.coverage_score;
  const runs = city.total_runs;
  if (typeof score !== "number" || typeof runs !== "number") return null;
  if (score >= 85 && runs >= 14) return "stable";
  if (score >= 50 || runs >= 7) return "runnable";
  if (runs > 0) return "seeding";
  return "planned";
}

function statusPillClass(status) {
  if (status == null || status === "") return "status-pill";
  const s = String(status).toLowerCase();
  return `status-pill sp-${s in CITY_STATUS_LABELS ? s : ""}`;
}

function statusPillLabel(status) {
  const s = (status ?? "").toLowerCase();
  const dot = { stable: "●", runnable: "●", seeding: "◐", planned: "○" }[s] ?? "";
  return `${dot} ${CITY_STATUS_LABELS[s] ?? status ?? ""}`.trim();
}

/**
 * Single optimized request: backend returns cities (with runs/clubs/slots), summary, and scoreboard
 * in one round-trip. Backend expects query param range_key (not range).
 */
function fetchNetworkData(params) {
  const rangeKey = params?.range ?? CONSOLE_DEFAULT_RANGE;
  return consoleApi.stats
    .networkOverview({ range_key: rangeKey })
    .then((res) => ({
      cities: Array.isArray(res?.cities) ? res.cities : [],
      scoreboard: Array.isArray(res?.scoreboard) ? res.scoreboard : [],
      summary: res?.summary ?? null,
      range: res?.range ?? rangeKey,
    }));
}

export default function NetworkView() {
  const navigate = useNavigate();
  const [range, setRange] = useState(CONSOLE_DEFAULT_RANGE);
  const fetcher = useCallback(() => fetchNetworkData({ range }), [range]);
  const { data, loading, error } = useConsoleFetch(fetcher, [range]);

  const statusByCityId = useMemo(() => {
    const m = new Map();
    if (Array.isArray(data?.cities)) data.cities.forEach((c) => m.set(c.id, deriveStatus(c)));
    return m;
  }, [data?.cities]);

  const runnableCount = useMemo(
    () => (data?.cities ?? []).filter((c) => ["runnable", "stable"].includes(statusByCityId.get(c.id))).length,
    [data?.cities, statusByCityId]
  );

  return (
    <ConsoleViewShell
      loading={loading && !data}
      error={error}
      loadingLabel="Loading network…"
    >
      {data ? (
        <div className="console-main-inner">
          <div className="network-heading-row">
            <div className="network-heading-main">
              <ConsolePageHeader
                label="RunCards Network"
                title="City Overview"
                sub="Each city is its own organism — seeded, stabilized, and maintained independently. Select a city to open its Ops Console."
              />
            </div>
            <Link to="/console/intelligence" className="tb-btn primary network-intelligence-cta">
              Intelligence Dashboard
            </Link>
          </div>
          <div className="section-label" style={{ marginTop: -8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ color: "var(--console-muted)", fontSize: 12 }}>
              Runs and coverage:
            </span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              aria-label="Filter by date range"
              className="filter-sel"
              style={{ minWidth: 140 }}
              disabled={loading}
            >
              {CONSOLE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {loading && <span style={{ color: "var(--console-muted)", fontSize: 11 }}>Updating…</span>}
          </div>

          {/* Summary: v2 metrics grid from DB */}
          <div className="metrics-grid" id="net-summary">
            {typeof data.summary?.total_cities === "number" && (
              <div className="mc">
                <div className="mc-label">Cities</div>
                <div className="mc-val">{data.summary.total_cities}</div>
                <div className="mc-sub">In network</div>
              </div>
            )}
            <div className="mc">
              <div className="mc-label">Runnable+</div>
              <div className="mc-val">{runnableCount}</div>
              <div className="mc-sub">Stable or runnable</div>
            </div>
            {typeof data.summary?.total_runs === "number" && (
              <div className="mc">
                <div className="mc-label">Total Runs</div>
                <div className="mc-val">{data.summary.total_runs}</div>
                <div className="mc-sub">Published</div>
              </div>
            )}
            {typeof data.summary?.total_clubs === "number" && (
              <div className="mc">
                <div className="mc-label">Clubs</div>
                <div className="mc-val">{data.summary.total_clubs}</div>
                <div className="mc-sub">Across cities</div>
              </div>
            )}
          </div>

          {/* Cities section label and grid */}
          <div className="section-label" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>Cities</span>
            <Link to="/console/cities" className="city-card-btn" style={{ margin: 0 }} onClick={(e) => e.stopPropagation()}>
              Manage city records (UUID)
            </Link>
          </div>
          <p style={{ color: "var(--console-muted)", fontSize: 13, marginBottom: 18, maxWidth: 640 }}>
            New markets with no clubs yet do not appear here — create the city under Manage city records, then open Clubs from that page to import from Strava.
          </p>
          <div className="city-grid" id="city-grid">
            {(data.cities ?? []).map((city) => {
              const status = statusByCityId.get(city.id);
              const slotsCovered = typeof city.slots_covered === "number" ? city.slots_covered : null;
              const runs = typeof city.total_runs === "number" ? city.total_runs : null;
              const clubs = typeof city.total_clubs === "number" ? city.total_clubs : null;
              const pct = slotsCovered != null && slotsCovered > 0 ? Math.min(100, Math.round((slotsCovered / 14) * 100)) : 0;
              const stageIdx = status != null ? CITY_STAGES.indexOf(status) : -1;
              const phaseDots = CITY_STAGES.map((s, i) => {
                const cls = i < stageIdx ? "done" : i === stageIdx ? "active" : "";
                return <span key={s} className={`phase-dot ${cls}`} title={CITY_STATUS_LABELS[s]} aria-hidden />;
              });

              return (
                <div
                  key={city.id}
                  className={`city-card ${status != null ? `status-${status}` : ""}`.trim()}
                  onClick={() => navigate(getConsoleCityUrl("overview", city.id))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(getConsoleCityUrl("overview", city.id));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="city-card-header">
                    <div>
                      <div className="city-name-big">{city.name}</div>
                      <div className="city-region-lbl">{city.region_label ?? ""}</div>
                    </div>
                    <span className={statusPillClass(status)}>{statusPillLabel(status)}</span>
                  </div>
                  <div className="city-stats-row">
                    <div className="city-stat">
                      <div className="city-stat-val">{runs != null ? runs : ""}</div>
                      <div className="city-stat-lbl">Runs</div>
                    </div>
                    <div className="city-stat">
                      <div className="city-stat-val">{typeof clubs === "number" ? clubs : ""}</div>
                      <div className="city-stat-lbl">Clubs</div>
                    </div>
                    <div className="city-stat">
                      <div className="city-stat-val">{typeof slotsCovered === "number" && slotsCovered > 0 ? `${slotsCovered}/14` : ""}</div>
                      <div className="city-stat-lbl">Slots</div>
                    </div>
                  </div>
                  {slotsCovered > 0 ? (
                    <div className="city-coverage-bar">
                      <div className="city-coverage-fill" style={{ width: `${pct}%` }} />
                    </div>
                  ) : (
                    <div style={{ height: 4, marginBottom: 14 }} />
                  )}
                  <div className="city-phase-row">
                    {phaseDots}
                    <span className="phase-lbl">{status != null && status in CITY_STATUS_LABELS ? CITY_STATUS_LABELS[status] : ""}</span>
                  </div>
                  <Link
                    to={getConsoleCityUrl("coverage", city.id)}
                    className="city-card-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View coverage
                  </Link>
                </div>
              );
            })}
          </div>

          {(data.cities ?? []).length === 0 && (
            <p className="section-label" style={{ marginTop: -8, marginBottom: 24, color: "var(--dim)" }}>
              No cities yet. Cities are derived from club locations in the database. For international launches, use a canonical city name
              (e.g. Paris) and optionally{" "}
              <Link to="/console/cities" className="underline">
                create a city UUID
              </Link>{" "}
              for stable console links — see the{" "}
              <a
                href={CONSOLE_INTL_ONBOARDING_DOC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                international onboarding guide
              </a>
              .
            </p>
          )}

          {/* Network Scoreboard — v2 section label */}
          <div className="section-label" style={{ marginBottom: 14 }}>
            Network Scoreboard
          </div>
          <div className="net-scoreboard">
            <div id="net-scoreboard-list">
              {(data.scoreboard ?? []).length === 0 ? (
                <p className="console-muted-text" style={{ fontSize: 13 }}>No scoreboard data yet.</p>
              ) : (
                (data.scoreboard ?? []).map((row, i) => {
                  const status = deriveStatus(row);
                  const runCounts = (data.scoreboard ?? []).map((r) => r.total_runs).filter((n) => typeof n === "number");
                  const maxRuns = runCounts.length > 0 ? Math.max(1, ...runCounts) : 1;
                  const rowRuns = typeof row.total_runs === "number" ? row.total_runs : 0;
                  const pct = Math.round((rowRuns / maxRuns) * 100);
                  return (
                    <div key={row.id} className="nsb-row">
                      <div className="nsb-rank">{i + 1}</div>
                      <div style={{ flex: "1.5" }}>
                        <div className="nsb-name">{row.name}</div>
                        <div className="nsb-region">{row.region_label ?? ""}</div>
                      </div>
                      <span className={statusPillClass(status)}>{statusPillLabel(status)}</span>
                      <div className="nsb-bar-wrap">
                        <div className="nsb-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="nsb-runs">{typeof row.total_runs === "number" ? `${row.total_runs} runs` : "runs"}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="console-view">
          <p className="console-muted-text">No data.</p>
        </div>
      )}
    </ConsoleViewShell>
  );
}
